import type { ComponentChildren, JSX } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';

type Props = {
  children: ComponentChildren;
  /** Stagger, in ms, applied as a CSS transition-delay. */
  delay?: number;
  as?:
    | 'div'
    | 'section'
    | 'li'
    | 'p'
    | 'article'
    | 'aside'
    | 'figure'
    | 'figcaption'
    | 'h2'
    | 'h3'
    | 'h4';
  class?: string;
  style?: JSX.CSSProperties;
};

/**
 * Fades content up the first time it enters the viewport. Reduced-motion is
 * handled in CSS, which neutralises the transition entirely.
 */
export function Reveal({
  children,
  delay = 0,
  as: Tag = 'div',
  class: className = '',
  style,
}: Props) {
  const ref = useRef<HTMLElement>(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || shown) return;
    if (typeof IntersectionObserver === 'undefined') {
      setShown(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setShown(true);
            io.disconnect();
          }
        }
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.08 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [shown]);

  return (
    <Tag
      ref={ref as never}
      class={`reveal ${shown ? 'is-in' : ''} ${className}`}
      style={{ ...style, '--reveal-delay': `${delay}ms` }}
    >
      {children}
    </Tag>
  );
}
